import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useQueryState } from "react-router-use-location-state";
import Alert from "@material-ui/lab/Alert";
import {
  Tabs,
  Tab,
  NavLink,
  LinearProgress,
  Heading,
  Button,
} from "../../components";
import { Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { useRouteMatch } from "react-router-dom";
import TaskDetails from "./TaskDetails";
import ExecutionSummary from "./ExecutionSummary";
import ExecutionJson from "./ExecutionJson";
import InputOutput from "./ExecutionInputOutput";
import clsx from "clsx";
import ActionModule from "./ActionModule";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";
import RightPanel from "./RightPanel";
import WorkflowDAG from "../../components/diagram/WorkflowDAG";
import StatusBadge from "../../components/StatusBadge";
import { Helmet } from "react-helmet";
import sharedStyles from "../styles";
import rison from "rison";
import { useWorkflow } from "../../data/workflow";
import { ArrowBack } from "@material-ui/icons";
import { usePushHistory } from "../../components/NavLink";
import WolkenSpinner from "../../components/WolkenSpinner";
import DefaultLoader from "../../components/DefaultLoader";
import { getParentUrl } from "../../utils/helpers";
import { useTabsList } from "../../contexts/TabsContext";
import NotFoundError from "./NotFoundError";
import RefreshAction from "./RefreshAction";
import Snackbar from "../../components/Snackbar";

const maxWindowWidth = window.innerWidth;
const INIT_DRAWER_WIDTH = 650;
// const TICKET_SUMMARY_INIT_DRAWER_WIDTH = 450;
const TICKET_SUMMARY_INIT_DRAWER_WIDTH = "50%";

const useStyles = makeStyles({
  header: sharedStyles.header,
  drawer: {
    zIndex: 999,
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    // width: (state) => (state.isFullWidth ? "100%" : state.drawerWidth),
    width: (state) => (state.isFullWidth ? "100%" : state.drawerWidth),
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: 10,
    justifyContent: "flex-end",
    // height: 80,
    height: 40,
    flexShrink: 0,
    boxShadow: "0 4px 8px 0 rgb(0 0 0 / 10%), 0 0 2px 0 rgb(0 0 0 / 10%)",
    zIndex: 1,
    backgroundColor: "#fff",
  },
  dragger: {
    display: (state) => (state.isFullWidth ? "none" : "block"),
    width: "5px",
    cursor: "ew-resize",
    padding: "4px 0 0",
    position: "absolute",
    height: "100%",
    zIndex: "100",
    backgroundColor: "#f4f7f9",
  },
  drawerMain: {
    paddingLeft: (state) => (state.isFullWidth ? 0 : 4),
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  drawerContent: {
    flex: 1,
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  content: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  contentShift: {
    marginRight: (state) => state.drawerWidth,
  },
  tabsContainer: {
    marginTop: "8px",
  },
  tabContent: {
    flex: 1,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  headerSubtitle: {
    fontFamily: "Poppins, sans-serif",
    marginBottom: 0,
    fontSize: 12,
  },
  backButton: {
    minWidth: "auto",
    padding: 0,
    marginTop: 2,
  },
  headerInfo: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
  },
  workflowTitle: {
    fontWeight: "normal",
    fontFamily: "Poppins, sans-serif",
    margin: 0,
    marginBottom: "4px",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  },
  executionIdRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "nowrap",
  },
  executionId: {
    fontFamily: "Poppins, sans-serif",
    margin: 0,
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flex: "0 1 auto",
  },
  statusBadgeWrapper: {
    flexShrink: 0,
  },
  headerContent: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    marginTop: 10,
    padding: "12px 15px",
    border: "1px solid #ccc",
    borderRadius: "12px",
    backgroundColor: "#fafafa",
  },
  headerTopRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    width: "100%",
    gap: 12,
    flexWrap: "wrap",
  },
  alertSection: {
    marginTop: "12px",
    width: "100%",
    "& .MuiAlert-root": {
      backgroundColor: "transparent",
      color: (state) => (state.isCompleted ? "#2e7d32" : "#d32f2f"),
      border: "1px solid #ccc",
      borderRadius: "8px",
    },
    "& .MuiAlert-icon": {
      color: (state) => (state.isCompleted ? "#2e7d32" : "#d32f2f"),
    },
  },

  headerLeft: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    flex: "1 1 auto",
    minWidth: 0,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    flexShrink: 0,
    justifyContent: "flex-end",
    minWidth: "max-content",
  },
  rightPanel: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  body: {
    width: "100vw",
    height: "100vh",
  },
  flexBody: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
});

function Execution(props) {
  const permissions = props?.permissions;
  const { tabsList } = useTabsList();
  const match = useRouteMatch();
  const navigate = usePushHistory();
  const { workflowID, view, rightDrawerWidth } = props;

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
  });

  const EnableExecutionActions = useMemo(() => {
    return permissions?.workflows?.execution_actions === "True";
  }, [permissions]);

  const flowlevelaccess = useMemo(
    () =>
      tabsList?.Execution?.workflows?.flowlevelaccess || {
        tasks: "True",
        summary: "True",
        workflowInputOutput: "True",
        JSON: "True",
      },
    [tabsList]
  );

  // build dynamic tabs list
  const availableTabs = useMemo(() => {
    const tabs = [];
    if (flowlevelaccess.tasks === "True")
      tabs.push({ key: "tasks", label: "Tasks" });
    if (flowlevelaccess.summary === "True")
      tabs.push({ key: "summary", label: "Summary" });
    if (flowlevelaccess.workflowInputOutput === "True")
      tabs.push({
        key: "workflowInputOutput",
        label: "Workflow Input/Output",
      });
    if (flowlevelaccess.JSON === "True")
      tabs.push({ key: "JSON", label: "JSON" });
    return tabs;
  }, [flowlevelaccess]);
  const {
    data: execution,
    isFetching,
    refetch: refresh,
    error,
  } = useWorkflow(!view ? match.params.id : workflowID);

  const [isFullWidth, setIsFullWidth] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(
    workflowID ? TICKET_SUMMARY_INIT_DRAWER_WIDTH : INIT_DRAWER_WIDTH
  );

  const [tabIndex, setTabIndex] = useQueryState("tabIndex", 0);
  const [selectedTaskRison, setSelectedTaskRison] = useQueryState("task", "");

  const dag = useMemo(() => {
    if (execution) {
      return new WorkflowDAG(execution, null, view);
    } else {
      return null;
    }
  }, [execution, view]);

  const selectedTask = useMemo(
    () => selectedTaskRison && rison.decode(selectedTaskRison),
    [selectedTaskRison]
  );

  const setSelectedTask = useCallback(
    (taskPointer) => {
      // Prevent right panel from opening in ticket summary view (when workflowID is provided)
      if (workflowID && view === "TICKET_SUMMARY") {
        return;
      }
      setSelectedTaskRison(rison.encode(taskPointer));
    },
    [workflowID, view, setSelectedTaskRison]
  );

  const handleTaskSelection = useMemo(
    () =>
      workflowID && view === "TICKET_SUMMARY" ? () => {} : setSelectedTask,
    [workflowID, view, setSelectedTask]
  );

  const handleClose = useCallback(() => {
    setSelectedTaskRison(null);
  }, [setSelectedTaskRison]);

  const handleRefreshAndClose = useCallback(() => {
    handleClose();
    refresh();
  }, [handleClose, refresh]);

  const deselectTaskAndCloseRightPanel = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const classes = useStyles({
    isFullWidth,
    drawerWidth,
    isCompleted: execution?.status === "COMPLETED",
  });

  const handleMousemove = useCallback(
    (e) => {
      // we don't want to do anything if we aren't resizing.
      if (!isResizing) {
        return;
      }

      // Stop highlighting
      e.preventDefault();
      const offsetRight =
        document.body.offsetWidth - (e.clientX - document.body.offsetLeft);
      const minWidth = 0;
      const maxWidth = maxWindowWidth - 100;

      if (offsetRight > minWidth && offsetRight < maxWidth) {
        setDrawerWidth(offsetRight);
      }
    },
    [isResizing]
  );

  const handleMousedown = useCallback((e) => setIsResizing(true), []);
  const handleDragEnd = useCallback((e) => {
    const rightDrawer = document.getElementById("slide-panel");
    if (rightDrawer) setDrawerWidth(rightDrawer.clientWidth);
  }, []);

  const handleFullScreen = useCallback(() => {
    setIsFullWidth(true);
  }, []);

  const handleFullScreenExit = useCallback(() => {
    setIsFullWidth(false);
  }, []);

  // On load and destroy only
  useEffect(() => {
    const mouseUp = (e) => setIsResizing(false);

    document.addEventListener("mousemove", handleMousemove);
    document.addEventListener("mouseup", mouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMousemove);
      document.removeEventListener("mouseup", mouseUp);
    };
  }, [handleMousemove]);

  useEffect(() => {
    // on load close right pannel
    handleClose();

    return () => {};
  }, []);

  const hasError = useMemo(() => {
    return !!error && !isFetching;
  }, [error, isFetching]);

  const handleActionError = useCallback((error) => {
    let errObj = error;

    // Attempt to parse the error if it's a stringified JSON
    if (typeof errObj === "string") {
      try {
        errObj = JSON.parse(errObj);
      } catch {
        // Not valid JSON, so we'll treat the original string as the potential message
      }
    }

    const extractedMessage =
      errObj?.message ||
      errObj?.error ||
      (typeof errObj === "string" ? errObj : null);

    const errorMessage =
      typeof extractedMessage === "string" && extractedMessage.length > 0
        ? extractedMessage
        : "An unexpected error occurred.";

    setSnackbar({ open: true, message: errorMessage });
  }, []);

  const handleSnackbarClose = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <>
      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        onClose={handleSnackbarClose}
        duration={5000}
      />

      {!workflowID ? (
        <>
          <Helmet>
            <title>Conductor UI - Execution - {match?.params?.id ?? ""}</title>
          </Helmet>
          {hasError ? (
            <NotFoundError
              bodyClass={classes.body}
              flexBodyClass={classes.flexBody}
              view={view}
              workflowID={workflowID}
              matchId={match?.params?.id}
              navigate={navigate}
              error={error}
            />
          ) : (
            <div
              className={clsx(classes.content, {
                [classes.contentShift]: !!selectedTask && !workflowID,
              })}
            >
              {isFetching && <LinearProgress />}
              {execution && (
                <>
                  <div className={classes.header} id="exe-header">
                    <div className={classes.headerContent}>
                      <div className={classes.headerTopRow}>
                        {/* Left side - Back button, Name and Status Badge */}
                        <div className={classes.headerLeft}>
                          <NavLink path={`/`} className={classes.backButton}>
                            <Tooltip title="Back to Executions">
                              <ArrowBack fontSize="medium" />
                            </Tooltip>
                          </NavLink>
                          <div className={classes.headerInfo}>
                            <Heading
                              level={0}
                              className={classes.workflowTitle}
                            >
                              {execution.workflowType || execution.workflowName}
                            </Heading>
                            <div className={classes.executionIdRow}>
                              <Heading
                                level={-1}
                                className={classes.executionId}
                              >
                                {execution.workflowId}
                              </Heading>
                              <span className={classes.statusBadgeWrapper}>
                                <StatusBadge status={execution.status} />
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Right side - Definition link and Actions */}
                        <div className={classes.headerRight}>
                          {execution.parentWorkflowId &&
                            EnableExecutionActions && (
                              <NavLink
                                absolutePath
                                newTab
                                path={`https://${getParentUrl()}/wolken/esd/w_flow?flowRedirectUrl=execution/${
                                  execution.parentWorkflowId
                                }`}
                              >
                                Parent Workflow
                              </NavLink>
                            )}
                          {EnableExecutionActions && (
                            <NavLink
                              absolutePath
                              newTab
                              path={`https://${getParentUrl()}/wolken/esd/w_flow?flowRedirectUrl=/workflowDef/${
                                execution.workflowName
                              }`}
                            >
                              Definition
                            </NavLink>
                          )}
                          {EnableExecutionActions && (
                            <RefreshAction
                              execution={execution}
                              onRefresh={handleRefreshAndClose}
                            />
                          )}

                          {EnableExecutionActions && (
                            <ActionModule
                              execution={execution}
                              triggerReload={handleRefreshAndClose}
                              onError={handleActionError}
                            />
                          )}
                        </div>
                      </div>

                      {/* Error Alert Section */}
                      {execution.reasonForIncompletion && (
                        <div className={classes.alertSection}>
                          <Alert
                            severity={
                              execution.status === "COMPLETED"
                                ? "success"
                                : execution.status === "FAILED"
                                ? "error"
                                : ""
                            }
                          >
                            {execution.reasonForIncompletion}
                          </Alert>
                        </div>
                      )}
                    </div>

                    <div className={classes.tabsContainer}>
                      <Tabs
                        value={tabIndex}
                        style={{ marginBottom: 0 }}
                        indicatorColor="primary"
                        textColor="primary"
                      >
                        {availableTabs.map((tab, idx) => (
                          <Tab
                            key={tab.key}
                            label={tab.label}
                            onClick={() => setTabIndex(idx)}
                          />
                        ))}
                      </Tabs>
                    </div>
                  </div>
                  <div className={classes.tabContent}>
                    {availableTabs[tabIndex]?.key === "tasks" && (
                      <TaskDetails
                        dag={dag}
                        execution={execution}
                        setSelectedTask={handleTaskSelection}
                        selectedTask={selectedTask}
                        refresh={refresh}
                        rightDrawerWidth={drawerWidth}
                        isFetching={isFetching}
                        deselectTaskAndCloseRightPanel={
                          deselectTaskAndCloseRightPanel
                        }
                      />
                    )}
                    {availableTabs[tabIndex]?.key === "summary" && (
                      <ExecutionSummary execution={execution} />
                    )}
                    {availableTabs[tabIndex]?.key === "workflowInputOutput" && (
                      <InputOutput execution={execution} />
                    )}
                    {availableTabs[tabIndex]?.key === "JSON" && (
                      <ExecutionJson execution={execution} />
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {/* if id available through props show just task */}
          {hasError ? (
            <NotFoundError
              bodyClass={classes.body}
              flexBodyClass={classes.flexBody}
              view={view}
              workflowID={workflowID}
              matchId={match?.params?.id}
              navigate={navigate}
              error={error}
            />
          ) : execution && !isFetching ? (
            <div style={{ overflow: "hidden" }}>
              <TaskDetails
                dag={dag}
                execution={execution}
                setSelectedTask={handleTaskSelection}
                selectedTask={selectedTask}
                showOnlyDiagram={Boolean(workflowID)}
                refresh={refresh}
                rightDrawerWidth={drawerWidth}
                deselectTaskAndCloseRightPanel={deselectTaskAndCloseRightPanel}
              />
            </div>
          ) : (!execution && isFetching) || (execution && isFetching) ? (
            <DefaultLoader
              displayModal={
                (!execution && isFetching) || (execution && isFetching)
              }
              message="Workflow is getting loaded"
            />
          ) : (
            <div className={`${classes.body} ${classes.flexBody}`}>
              No Execution Data
            </div>
          )}
        </>
      )}
      {selectedTask && !workflowID && (
        <div
          id={workflowID ? "right-drawer" : "slide-panel"}
          className={classes.drawer}
        >
          <div
            id="dragger"
            onMouseDown={(event) => handleMousedown(event)}
            onMouseUp={(event) => handleDragEnd(event)}
            className={classes.dragger}
          />
          <div className={classes.drawerMain}>
            <div className={classes.drawerHeader}>
              {isFullWidth ? (
                <Tooltip title="Restore sidebar">
                  <IconButton onClick={() => handleFullScreenExit()}>
                    <FullscreenExitIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Maximize sidebar">
                  <IconButton onClick={() => handleFullScreen()}>
                    <FullscreenIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Close sidebar">
                <IconButton onClick={() => handleClose()}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </div>
            <div className={classes.drawerContent}>
              <RightPanel
                className={classes.rightPanel}
                selectedTask={selectedTask}
                dag={dag}
                onTaskChange={setSelectedTask}
                view={view}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default React.memo(Execution);
